import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import Header from "../ReusedComponents/Header";
import { DecryptED } from "../../DecryptJS/Decrypt";
import {
  checkMailConfig,
  fetchEnabledEmailAlertTypes,
  saveEnabledEmailAlertTypes,
  fetchEmailAlertFailures,
} from "../ReusedComponents/emailAlertSettings";

export default function EmailAlertSettings() {
  const { sid, uid } = useParams();
  const history = useHistory();
  const decSid = (function () { try { return DecryptED(sid); } catch (e) { return sid; } })();

  const [allTypes, setAllTypes] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mailConfigured, setMailConfigured] = useState(false);
  const [failures, setFailures] = useState([]);

  useEffect(() => {
    var staffPromise = axios.post("https://topschool.prisms.in/rest/index.php/user_list.json?", {
      fun_name: "GetStaffDetails",
      sid: sid,
    }, { timeout: 20000 }).then(function (res) {
      var staffList = res.data && Array.isArray(res.data.Staff_Details) ? res.data.Staff_Details : [];
      return Array.from(new Set(staffList.map(function (s) { return s.type; }).filter(Boolean)));
    }).catch(function () {
      return [];
    });

    Promise.all([
      staffPromise,
      fetchEnabledEmailAlertTypes(decSid),
      checkMailConfig(),
      fetchEmailAlertFailures(decSid),
    ]).then(function (results) {
      var types = results[0];
      var enabled = results[1];
      var mailStatus = results[2];
      var recentFailures = results[3];

      setAllTypes(types);
      var initial = {};
      types.forEach(function (t) {
        initial[t] = enabled.indexOf(t) !== -1;
      });
      setSelected(initial);
      setMailConfigured(mailStatus.configured);
      setFailures(recentFailures);
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid]);

  function toggle(type) {
    if (!mailConfigured) return;
    setSelected(function (prev) { return { ...prev, [type]: !prev[type] }; });
  }

  function handleSelectAll() {
    if (!mailConfigured) return;
    var all = {};
    allTypes.forEach(function (t) { all[t] = true; });
    setSelected(all);
  }

  function handleSelectNone() {
    var none = {};
    allTypes.forEach(function (t) { none[t] = false; });
    setSelected(none);
  }

  function handleSave() {
    if (!mailConfigured) return;
    var enabled = Object.keys(selected).filter(function (t) { return selected[t]; });
    setSaving(true);
    saveEnabledEmailAlertTypes(decSid, enabled).then(function (ok) {
      setSaving(false);
      if (ok) {
        Swal.fire({
          title: "Saved",
          text: "Staff of the checked types will now receive an email alert (in addition to SMS) when assigned a ticket.",
          icon: "success",
        });
      } else {
        Swal.fire({
          title: "Save failed",
          text: "Could not save this setting — please check your connection and try again.",
          icon: "error",
        });
      }
    });
  }

  return (
    <div className="totalTicketingModule">
      <Header
        title="Email Alert Settings"
        options={[
          { title: "Create Ticket", url: `/tickets/admin/creating/${sid}/${uid}` },
          { title: "Reports", url: `/tickets/admin/reports/${sid}/${uid}` },
        ]}
        ChangePage={(params) => history.push(params.url)}
      />
      <div style={{ padding: "24px 32px", maxWidth: 560, fontFamily: "'Plus Jakarta Sans', 'Segoe UI', sans-serif" }}>
        <h3 style={{ margin: "0 0 6px", color: "#0b2545" }}>Email Alerts by Staff Type</h3>
        <p style={{ color: "#6b7280", fontSize: 13, marginBottom: 16 }}>
          Choose which staff types get an email alert (in addition to the existing SMS) when
          assigned a ticket. Unchecked types keep getting SMS only, exactly as today.
        </p>

        {!loading && !mailConfigured && (
          <div style={{
            background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b",
            borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 16,
          }}>
            Email alerts are unavailable — the mail server is not configured for this system.
            Enabling is disabled until an administrator sets up email configuration.
          </div>
        )}

        {!loading && mailConfigured && failures.length > 0 && (
          <div style={{
            background: "#fffbeb", border: "1px solid #fde68a", color: "#92400e",
            borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 16,
          }}>
            {failures.length} email alert{failures.length === 1 ? "" : "s"} failed to send recently — check your mail configuration.
          </div>
        )}

        {loading ? (
          <p>Loading staff types…</p>
        ) : allTypes.length === 0 ? (
          <p>No staff types found for this school.</p>
        ) : (
          <>
            <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
              <span
                onClick={handleSelectAll}
                style={{ fontSize: 12, color: mailConfigured ? "#1e5597" : "#9ca3af", cursor: mailConfigured ? "pointer" : "default", fontWeight: 600 }}
              >
                Select all
              </span>
              <span onClick={handleSelectNone} style={{ fontSize: 12, color: "#1e5597", cursor: "pointer", fontWeight: 600 }}>
                Select none
              </span>
            </div>
            <div style={{
              border: "1px solid #e2eaf4", borderRadius: 10, padding: "8px 16px",
              background: mailConfigured ? "#fff" : "#f3f4f6", opacity: mailConfigured ? 1 : 0.6,
            }}>
              {allTypes.map(function (t) {
                return (
                  <label key={t} style={{ display: "flex", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #f4f7fb", cursor: mailConfigured ? "pointer" : "not-allowed" }}>
                    <input
                      type="checkbox"
                      checked={!!selected[t]}
                      disabled={!mailConfigured}
                      onChange={function () { toggle(t); }}
                      style={{ marginRight: 10, width: 16, height: 16 }}
                    />
                    <span style={{ fontSize: 13, color: "#0b2545" }}>{t}</span>
                  </label>
                );
              })}
            </div>
            <button
              onClick={handleSave}
              disabled={saving || !mailConfigured}
              style={{
                marginTop: 20, padding: "10px 24px", background: mailConfigured ? "#1e3a5f" : "#9ca3af", color: "#fff",
                border: "none", borderRadius: 8, cursor: (saving || !mailConfigured) ? "default" : "pointer", fontSize: 13, fontWeight: 700,
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
